import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BiArrowBack, BiPlus, BiSearch } from "react-icons/bi";
import { Card } from "@material-tailwind/react";

import { Colors } from "../../../assets/Colors";
import { fetchGet, fetchPost, fetchPut } from "../../../fetchers";

/**
 * Master Port Service.
 *
 * Daftar layanan pada aktivitas Port Services di form Quotation. Sebelum ini
 * daftarnya hardcode di frontend (tiga salinan), sehingga menambah layanan berarti
 * ubah kode + build + deploy.
 *
 * Halaman ini sengaja BERDIRI SENDIRI, tidak memakai TableCard/ModalForm generik
 * seperti master lama: keduanya terkunci pada bentuk tiga kolom dan flag per-master
 * (`dropDownWaste`, dst). Port Service butuh enam field termasuk dropdown
 * FormulaType, dan menambah flag baru ke komponen bersama akan menyentuh semua
 * master lain.
 */

// Dasar penagihan. Daftar ini HARUS sama dengan FormulaTypeValid di
// models/master/PortService.go — backend menolak nilai di luar daftar.
const FORMULA_OPTIONS = [
  { value: "QTY", label: "QTY — per kuantitas", hint: "Paling umum: Stevedoring, Receiving, Delivery" },
  { value: "DURATION_HOURS", label: "DURATION_HOURS — per jam", hint: "Dihitung dari selisih jam mulai–selesai" },
  { value: "METER", label: "METER — selisih meter", hint: "Volume = meter akhir − meter awal" },
  { value: "DURATION_DAYS", label: "DURATION_DAYS — rentang hari", hint: "Selisih hari mulai–selesai (untuk Vessel satuan Day)" },
  { value: "DURATION", label: "DURATION — per hari", hint: "" },
  { value: "DURATION_MONTHS", label: "DURATION_MONTHS — per bulan", hint: "" },
  { value: "DURATION_HOURS_X_QTY", label: "DURATION_HOURS_X_QTY — jam × qty", hint: "" },
  { value: "QTY_X_MULT", label: "QTY_X_MULT — qty × pengali", hint: "" },
  { value: "PER_DATE", label: "PER_DATE — per tanggal", hint: "" },
];

const KOSONG = {
  PortServiceID: "",
  Kode: "",
  Nama: "",
  FormulaType: "QTY",
  ServiceType: "V",
  DefaultSatuanID: "",
  IsActive: true,
  SortOrder: 0,
  DipakaiQO: 0,
};

const ModalPortService = ({ show, mode, data, busy, onSimpan, onBatal }) => {
  const [form, setForm] = useState(KOSONG);

  useEffect(() => {
    if (show) setForm(mode === "edit" ? { ...KOSONG, ...data } : KOSONG);
  }, [show, mode, data]);

  if (!show) return null;

  const isEdit = mode === "edit";
  // Kode terkunci saat edit. QoLine menyimpan salinan teksnya tanpa foreign key,
  // jadi mengubah Kode akan memutus seluruh QO yang memakainya — backend pun
  // tidak menyediakan jalurnya.
  const kodeTerkunci = isEdit;
  const kodeKosong = !form.Kode.trim();
  const namaKosong = !form.Nama.trim();
  const kodeBerspasi = /\s/.test(form.Kode);
  const takValid = namaKosong || (!isEdit && (kodeKosong || kodeBerspasi));

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const formulaTerpilih = FORMULA_OPTIONS.find((o) => o.value === form.FormulaType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {isEdit ? "Ubah Layanan Port Service" : "Tambah Layanan Port Service"}
        </h3>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Kode <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.Kode}
              disabled={kodeTerkunci}
              onChange={(e) => set("Kode", e.target.value)}
              placeholder="Lashing_Securing"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
            />
            {kodeTerkunci ? (
              <p className="text-xs text-gray-500 mt-1">
                Kode tidak bisa diubah — {form.DipakaiQO} baris Quotation menyimpannya
                sebagai teks. Mengubahnya akan memutus QO lama.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Tanpa spasi, pakai garis bawah. Nilai inilah yang tersimpan di Quotation
                dan tidak bisa diubah lagi setelah dipakai.
              </p>
            )}
            {!isEdit && kodeBerspasi && (
              <p className="text-xs text-red-600 mt-1">Kode tidak boleh mengandung spasi.</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Nama tampilan <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.Nama}
              onChange={(e) => set("Nama", e.target.value)}
              placeholder="Lashing &amp; Securing"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Dasar penagihan <span className="text-red-500">*</span>
            </label>
            <select
              value={form.FormulaType}
              onChange={(e) => set("FormulaType", e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              {FORMULA_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-xs text-amber-700 mt-1">
              {formulaTerpilih?.hint
                ? formulaTerpilih.hint + ". "
                : ""}
              Kolom ini menentukan ANGKA TAGIHAN pada Job Request — salah pilih baru
              ketahuan saat invoice terbit.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Urutan tampil</label>
              <input
                type="number"
                value={form.SortOrder}
                onChange={(e) => set("SortOrder", parseInt(e.target.value || "0", 10))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!form.IsActive}
                  onChange={(e) => set("IsActive", e.target.checked)}
                />
                Aktif (muncul di dropdown)
              </label>
            </div>
          </div>
          {isEdit && !form.IsActive && form.DipakaiQO > 0 && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
              Dinonaktifkan tapi masih dipakai {form.DipakaiQO} baris Quotation. Itu aman:
              QO lama tetap terbaca dan dasar penagihannya tetap dari sini — layanan ini
              hanya tak lagi ditawarkan untuk QO baru.
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onBatal}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Batal
          </button>
          <button
            onClick={() => onSimpan(form)}
            disabled={takValid || busy}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PortServicePage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState({ show: false, mode: "add", data: KOSONG });
  const [busy, setBusy] = useState(false);

  const muat = useCallback(async (cari = "") => {
    setLoading(true);
    try {
      // count 500: master ini kecil (puluhan baris) dan pengurutannya bermakna,
      // jadi paginasi hanya akan menyulitkan tanpa memberi manfaat.
      const params = new URLSearchParams({ start: "0", count: "500", search: cari });
      const res = await fetchGet(`Master/PortService/getAll?${params.toString()}`);
      setRows(res?.data ?? []);
    } catch (_) {
      // fetchGet sudah memunculkan toast-nya sendiri.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { muat(""); }, [muat]);

  const simpan = async (form) => {
    setBusy(true);
    try {
      if (modal.mode === "edit") {
        await fetchPut("Master/PortService", {
          PortServiceID: form.PortServiceID,
          Nama: form.Nama,
          FormulaType: form.FormulaType,
          ServiceType: form.ServiceType || "V",
          DefaultSatuanID: form.DefaultSatuanID || "",
          IsActive: !!form.IsActive,
          SortOrder: Number(form.SortOrder) || 0,
        });
      } else {
        await fetchPost("Master/PortService", {
          Kode: form.Kode.trim(),
          Nama: form.Nama.trim(),
          FormulaType: form.FormulaType,
          ServiceType: form.ServiceType || "V",
          DefaultSatuanID: form.DefaultSatuanID || "",
        });
      }
      setModal({ show: false, mode: "add", data: KOSONG });
      muat(search);
    } catch (_) {
      // Modal dibiarkan terbuka: penyebab tersering adalah Kode bentrok, dan
      // pengguna perlu isian yang sudah diketiknya.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="md:ml-64">
      <ModalPortService
        show={modal.show}
        mode={modal.mode}
        data={modal.data}
        busy={busy}
        onSimpan={simpan}
        onBatal={() => setModal({ show: false, mode: "add", data: KOSONG })}
      />

      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full" />
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <Card>
            <div className="px-6 py-4 border-b flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate("/master")}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm hover:bg-gray-100"
                  title="Kembali ke Master"
                >
                  <BiArrowBack />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Master Port Service</h2>
                  <p className="text-xs text-gray-500">
                    Daftar layanan pada aktivitas Port Services di form Quotation.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <BiSearch className="absolute left-2 top-2.5 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && muat(search)}
                    placeholder="Cari kode / nama"
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-56"
                  />
                </div>
                <button
                  onClick={() => setModal({ show: true, mode: "add", data: KOSONG })}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  <BiPlus /> Tambah
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Urutan</th>
                    <th className="px-4 py-3 text-left">Kode</th>
                    <th className="px-4 py-3 text-left">Nama</th>
                    <th className="px-4 py-3 text-left">Dasar Penagihan</th>
                    <th className="px-4 py-3 text-right">Dipakai QO</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Memuat…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Tidak ada data</td></tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.PortServiceID} className={r.IsActive ? "" : "bg-gray-50 text-gray-500"}>
                        <td className="px-4 py-3">{r.SortOrder}</td>
                        <td className="px-4 py-3 font-mono text-xs">{r.Kode}</td>
                        <td className="px-4 py-3">{r.Nama}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-gray-100 rounded px-2 py-1">
                            {r.FormulaType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{r.DipakaiQO}</td>
                        <td className="px-4 py-3">
                          {r.IsActive ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Aktif</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Nonaktif</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setModal({ show: true, mode: "edit", data: r })}
                            className="border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-3 py-1.5 text-xs font-medium"
                          >
                            Ubah
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 text-xs text-gray-500 border-t">
              Layanan yang sudah dipakai Quotation tidak bisa dihapus atau diubah kodenya.
              Untuk memensiunkan layanan, hilangkan centang <b>Aktif</b> — QO lama tetap
              terbaca dan dasar penagihannya tetap benar.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PortServicePage;
